import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import shortid from 'shortid'

import styles from '../component-styles/generator-updates-request-list.module.scss'
import {
  getUpdateRequestsAsync,
  setUpdateRequestsAsync,
  sendUpdateRequestsAsync,
  updateGeneratorParameterSelectAddOptionAsync,
} from '../store/actions'
import { SupportState, Requests } from '../store/support-reducer'
import { CampaignCodesState } from '../store/campaign-codes-reducer'
import { GraphQLState } from '../store/graphql-reducer'
import Button, { ButtonRow } from './button'
import Input, { Label } from './input'
import Row, { Slot } from './row'
import {
  getItemByKeyValue,
  prepareInput,
  getValidationChecksObject,
} from '../helpers'
import { ValidationChecks, GeneratorFields } from '../api-client/api-types'
import Heading from './heading'
import Modal, { ModalHead, ModalBody, ModalFooter } from './modal'
import ConfirmationButtons from './confirmation-buttons'
import ToggleBox from './toggle-box'
import HeaderPanel, { Panel } from './header-panel'
import ToolTip from './tooltip'
import { BulletCounter } from './counter'
import EditCodeStructureModal from './edit-code-structure-modal'
import useMobile from '../hooks/useMobile'

interface SendSetUpdate {
  dismissed: boolean
  requestID: string
  changeRequests: Requests[]
  forceCompleted: boolean
  updateData?: {
    approved: boolean
    changeRequests: Requests[]
    message: string
    requestID: string
  } | null
}

interface UpdateRequestProps {
  data: any
  validation?: ValidationChecks[] | undefined | null
  sendSetUpdate: (SendSetUpdate) => void
  fields: GeneratorFields[] | undefined
  isMobile: boolean
}

export function UpdateRequest({
  data,
  validation,
  sendSetUpdate,
  fields,
  isMobile,
}: UpdateRequestProps): React.ReactElement | null {
  const [form, setForm] = useState(data)

  const [changeRequest, setChangeRequest] = useState<null | any>(null)
  const [active, setActive] = useState(false)
  const [userMessage, setUserMessage] = useState('')
  const { requestedBy, requestDate, requestNote, changeRequests } = form

  const setFormValue = (fieldIndex, optionIndex, val) => {
    const newChangeRequests = [...changeRequests]
    const newChangeRequestItem =
      newChangeRequests[fieldIndex].newOptions[optionIndex]
    newChangeRequests[fieldIndex].newOptions[optionIndex] = {
      ...newChangeRequestItem,
      ...val,
    }
    const newForm = {
      ...form,
      changeRequests: newChangeRequests,
    }
    setForm(newForm)
    return newForm
  }

  const { requestID } = data || { requestID: '' }

  const { approved: approve } = changeRequest || { approved: true }

  const showBox = useMemo(() => {
    let hasActiveRequests = false
    changeRequests.forEach((field, fieldIndex) => {
      const hasChildren =
        field.newOptions.filter((option) => {
          return typeof option.approved === 'undefined'
        }).length > 0

      if (hasChildren) {
        hasActiveRequests = true
      }
    })
    return hasActiveRequests
  }, [changeRequests])

  if (!showBox || data.length === 0) {
    return null
  }

  return (
    <>
      <ToggleBox
        open
        disableToggle
        className={styles.accordion}
        type="white"
        heading={
          <ButtonRow className={styles.headerRow}>
            <div>
              <h3 className={styles.requestTitle}>
                <span className={styles.requestedBy}>{requestedBy}</span> on{' '}
                <span className={styles.date}>
                  {moment(requestDate).format('ddd Do MMM YYYY')}
                </span>
              </h3>
            </div>
            <Button
              type="clear"
              className={styles.dismissButton}
              onClick={(): void => {
                const requestData = {
                  changeRequests,
                  dismissed: true,
                  forceCompleted: false,
                  requestID,
                }
                sendSetUpdate(requestData)
              }}
            >
              Dismiss request
            </Button>
          </ButtonRow>
        }
      >
        <div className={styles.box}>
          {requestNote && requestNote !== '' && (
            <p className={styles.requestedNote}>
              <strong>Request note:</strong> {requestNote}
            </p>
          )}
          <div className={styles.requestSummary}>
            {changeRequests.map((field, fieldIndex) => {
              const hasChildren =
                field.newOptions.filter((option) => {
                  return typeof option.approved === 'undefined'
                }).length > 0

              if (!hasChildren) {
                return null
              }

              return (
                <div
                  key={`${field.fieldID}`}
                  className={styles.requestSummaryOuterRow}
                >
                  {!isMobile && (
                    <Row>
                      <Slot className={styles.optionNameSlot}>
                        <Label>
                          <span className={styles.title}>
                            Dropdown name for {field.fieldName}
                          </span>
                          <ToolTip
                            className={styles.tooltip}
                            message="The name Uplifter users will see on the *Track>Create* page when selecting dropdown options to create a campaign link. We recommend not using abbreviated names."
                          />
                        </Label>
                      </Slot>
                      <Slot>
                        <Label>
                          <span className={styles.title}>Dropdown code</span>
                          <ToolTip
                            className={styles.tooltip}
                            message="This is what characters will be in the code when this dropdown is selected. Some users like to use shortened codes."
                          />
                        </Label>
                      </Slot>
                      <Slot className={styles.actionSlot}>
                        <h3 className={styles.title}>Actions</h3>
                      </Slot>
                    </Row>
                  )}
                  {field.newOptions.map((option, optionIndex) => {
                    const { approved } = option
                    if (typeof approved !== 'undefined') {
                      return null
                    }
                    const currentField = getItemByKeyValue(
                      fields,
                      'fieldID',
                      field.fieldID,
                    )
                    let fieldValueError = false
                    if (currentField !== -1) {
                      const found = getItemByKeyValue(
                        currentField.selectFields,
                        'optionValue',
                        option.optionValue,
                      )
                      fieldValueError = found !== -1
                    }

                    return (
                      <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${field.fieldID}${optionIndex}`}
                        className={styles.requestSummaryRow}
                      >
                        <Row>
                          <Slot className={styles.optionNameSlot}>
                            {isMobile && (
                              <Label>
                                <span className={styles.title}>
                                  Dropdown name for {field.fieldName}
                                </span>
                                <ToolTip
                                  className={styles.tooltip}
                                  message="The name Uplifter users will see on the *Track>Create* page when selecting dropdown options to create a campaign link. We recommend not using abbreviated names."
                                />
                              </Label>
                            )}
                            <Input
                              id="optionName"
                              name="optionName"
                              type="text"
                              value={option.optionName}
                              placeholder="Dropdown option name"
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                const { value: val } = event.target
                                const optionValue = prepareInput(
                                  val,
                                  validation,
                                )
                                setFormValue(fieldIndex, optionIndex, {
                                  optionName: val,
                                  optionValue,
                                })
                              }}
                            />
                          </Slot>
                          <Slot>
                            {isMobile && (
                              <Label>
                                <span className={styles.title}>
                                  Dropdown code
                                </span>
                                <ToolTip
                                  className={styles.tooltip}
                                  message="This is what characters will be in the code when this dropdown is selected. Some users like to use shortened codes."
                                />
                              </Label>
                            )}
                            <Input
                              key={option.optionName}
                              id="optionValue"
                              name="optionValue"
                              type="text"
                              value={option.optionValue}
                              placeholder="Code"
                              error={fieldValueError}
                              beforeChange={(inputValue: string) => {
                                return prepareInput(inputValue, validation)
                              }}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                const { value: val } = event.target
                                setFormValue(fieldIndex, optionIndex, {
                                  optionValue: val,
                                })
                              }}
                            />
                            {fieldValueError && (
                              <p className={styles.footNoteError}>
                                This code is already being used.
                              </p>
                            )}
                          </Slot>
                          <Slot>
                            <ButtonRow className={styles.buttonRow}>
                              <Button
                                type="clear"
                                className={styles.manageButton}
                                onClick={(): void => {
                                  setChangeRequest({
                                    approved: false,
                                    fieldIndex,
                                    optionIndex,
                                  })
                                  setActive(true)
                                }}
                              >
                                Reject
                              </Button>
                              <Button
                                type="addGreen"
                                disabled={
                                  !(
                                    option.optionName !== '' &&
                                    option.optionValue !== ''
                                  ) || fieldValueError
                                }
                                className={styles.manageButton}
                                onClick={(): void => {
                                  setChangeRequest({
                                    approved: true,
                                    fieldIndex,
                                    optionIndex,
                                  })
                                  setActive(true)
                                }}
                              >
                                Approve
                              </Button>
                            </ButtonRow>
                          </Slot>
                        </Row>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      </ToggleBox>
      {active && (
        <Modal
          type={approve ? 'green' : 'white'}
          narrow
          active={active}
          toggleActive={() => {
            setActive(false)
          }}
        >
          <ModalHead>
            <Heading type={3} align="left" className={styles.heading}>
              {approve ? (
                <> Add an approval message</>
              ) : (
                <> Why are you rejecting this?</>
              )}
            </Heading>
          </ModalHead>
          <ModalBody>
            <Label modalHeading id="parameter-reason" optional="(optional)">
              Leave message for the user
            </Label>
            <textarea
              id="parameter-reason"
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                const { value: val } = event.target
                setUserMessage(val)
              }}
              placeholder="Leave comment..."
              value={userMessage}
            />
          </ModalBody>
          <ModalFooter>
            <ConfirmationButtons
              type="simple"
              onYes={(): void => {
                setActive(false)
                // eslint-disable-next-line
                // debugger
                const { fieldIndex, optionIndex, approved } = changeRequest
                const useChangeRequest = setFormValue(fieldIndex, optionIndex, {
                  approved,
                  message: userMessage,
                })

                const updateChangeRequest = {
                  ...useChangeRequest.changeRequests[fieldIndex],
                }
                const updateData = {
                  approved,
                  changeRequests: [
                    {
                      ...updateChangeRequest,
                      newOptions: [
                        { ...updateChangeRequest.newOptions[optionIndex] },
                      ],
                    },
                  ],
                  message: userMessage,
                  requestID,
                }

                sendSetUpdate({
                  ...useChangeRequest,
                  dismissed: false,
                  forceCompleted: false,
                  updateData,
                })
                setUserMessage('')
              }}
              yesLabel={approve ? 'Approve' : 'Reject'}
              onNo={(): void => {
                setActive(false)
              }}
              noLabel="Go back"
            />
          </ModalFooter>
        </Modal>
      )}
    </>
  )
}

export default function GeneratorUpdatesRequestList(): React.ReactElement | null {
  const isMobile = useMobile(769)

  const dispatch = useDispatch()
  const [showSettings, setShowSettings] = useState(false)

  const {
    graphql: { currentUser },
    campaignCodes: { generatedStructure },
    support: { pendingRequests },
  }: {
    graphql: GraphQLState
    campaignCodes: CampaignCodesState
    support: SupportState
  } = useSelector((s: any): any => s)

  useEffect(() => {
    dispatch(getUpdateRequestsAsync())
  }, [])

  const validation = getValidationChecksObject(generatedStructure)
  const currentAccountPermission = currentUser?.currentAccountPermission || ''

  const isAdminUser =
    currentAccountPermission === 'admin' ||
    currentAccountPermission === 'support'

  if (!isAdminUser || pendingRequests.length === 0) {
    return null
  }

  return (
    <>
      <HeaderPanel>
        <Panel>
          <Heading type={3} align="left" className={styles.heading}>
            Requested dropdown values
            <ToolTip
              showUnder
              className={styles.tooltip}
              message="Dropdown values regular users have requested to be added to the *Track>Create* page. If similar values already exist, you can reject it with a comment."
            />
            <BulletCounter
              count={pendingRequests.length}
              className={styles.counter}
            />
          </Heading>
        </Panel>
        <Panel>
          <ButtonRow className={styles.panelButtonRow}>
            <Button
              onClick={(): void => {
                setShowSettings(!showSettings)
              }}
              type="edit"
            >
              Edit code structure
            </Button>
          </ButtonRow>
        </Panel>
      </HeaderPanel>
      <div className={styles.toggleWrapper}>
        {pendingRequests.map((item) => {
          return (
            <UpdateRequest
              isMobile={isMobile}
              fields={generatedStructure?.paramDefs}
              validation={validation}
              data={item}
              key={shortid.generate()}
              sendSetUpdate={({
                dismissed,
                requestID,
                changeRequests,
                forceCompleted,
                updateData = null,
              }: SendSetUpdate) => {
                const data = {
                  changeRequests,
                  dismissed,
                  forceCompleted,
                  requestID,
                }
                if (updateData) {
                  dispatch(sendUpdateRequestsAsync(updateData))
                  if (
                    updateData.approved &&
                    updateData.changeRequests.length > 0
                  ) {
                    const request = updateData.changeRequests[0]
                    if (request.newOptions.length > 0) {
                      const addOptionData = {
                        action: 'user-request',
                        fieldID: request.fieldID,
                        newOption: {
                          hide: false,
                          optionName: request.newOptions[0].optionName,
                          optionValue: request.newOptions[0].optionValue,
                        },
                      }
                      dispatch(
                        updateGeneratorParameterSelectAddOptionAsync({
                          ...addOptionData,
                        }),
                      )
                    }
                  }
                }
                dispatch(setUpdateRequestsAsync(data))
              }}
            />
          )
        })}
      </div>
      {showSettings && (
        <EditCodeStructureModal
          onHide={() => {
            setShowSettings(false)
          }}
        />
      )}
    </>
  )
}
